import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography, Grid, Button, Chip } from '@material-ui/core';
import { useSize } from 'react-use';

// CONSTANTS
import { actions, triggered } from '@/constants/analytics';
import { ROUTE_PROFILE_SUBSCRIPTION_DETAIL } from '@/constants/routes';
import { appsListStatuses } from '@/constants/app';

// UTILITY
import { getLink } from '@/utility/routes';
import { isEvent } from '@/utility/common';

// STORE
import { AppDetailsActions } from '@/store/actions';

// HOC
import TruncatedList from '@/hoc/TruncatedList';
import Guard from '@/hoc/Guard';

// HOOKS
import useDictionary from '@/hooks/useDictionary';

// COMPONENTS
import AddRemoveButton from '@/components/Buttons/AddRemoveButton';
import Link from '@/components/Link/Link';

// STYLES
import { AppCardContentStyled } from '../styles';

// HELP
import AppCard from './AppCard';

function getList(list = [], count = 1) {
  const listLength = list.length;
  const moreThanNecessary = listLength > count;

  const newList = list.slice(0, moreThanNecessary ? count - 1 : count);

  return moreThanNecessary
    ? [
        ...newList,
        {
          rest_count: `+${listLength - count + 1}`,
        },
      ]
    : newList;
}

function AppCardContent({
  data,
  className,
  location,
  onUpdateStatus,
  onRemoveStatus,
  LinkProps,
  hideControls,
}) {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { isSimpleView, partner, partnerSettings, statusesLoadings } = useSelector(store => ({
    isSimpleView: store.app.isSimpleView,
    partner: store.partner.data,
    partnerSettings: store.partner.data?.settings || {},
    statusesLoadings: store.appDetails.loadings.statusesLoadings,
  }));
  const genresData = useDictionary(data.genres, 'genres');
  const devicesData = useDictionary(data.devices, 'devices');
  const hasGenres = !!genresData?.length;
  const hasDevices = !!devicesData?.length;

  const initialContentWidth = 238;
  const added = data.list_status === appsListStatuses.have;
  const hasPartner = !!partner.slug;
  const isSubscribed = data.user_subscribed;

  let useAuthorizing = true;
  let useSubscription = true;

  if (hasPartner) {
    useAuthorizing = !!partnerSettings.use_authorizing;
    useSubscription = !!partnerSettings.use_authorizing && !!partnerSettings.use_subscription;
  }

  const [sizedDevices] = useSize(
    ({ width }) => {
      if (!hasDevices) return <></>;

      const cellWidth = 64;
      const cellPadding = 3;
      const countItemsInLine = Math.floor((width + cellPadding) / (cellWidth + cellPadding)) * 2;
      const devices = getList(devicesData, countItemsInLine);

      return (
        <div className="app-card-content__list">
          <Grid container spacing={1}>
            {devices.map(({ logo_url, rest_count } = {}, idx) => {
              return (
                <Grid item key={idx}>
                  <div className="app-card-content__card app-card-content__card--device">
                    {rest_count ? (
                      <Typography variant="h6" component="p" color="textPrimary">
                        {rest_count}
                      </Typography>
                    ) : (
                      <img src={logo_url} alt="device-logo" />
                    )}
                  </div>
                </Grid>
              );
            })}
          </Grid>
        </div>
      );
    },
    { width: initialContentWidth },
  );

  const handleUpdateAppListStatus = e => {
    if (isEvent(e)) {
      e.preventDefault();
      e.stopPropagation();
    }

    const requestData = {
      data: {
        apps: [{ app_id: data.id, status: appsListStatuses.have }],
      },
      callback: () => onUpdateStatus(data, appsListStatuses.have),
    };

    dispatch(AppDetailsActions.appListUpdateStatus.action(requestData));
  };

  const handleRemoveAppListStatus = e => {
    if (isEvent(e)) {
      e.preventDefault();
      e.stopPropagation();
    }

    const requestData = {
      app_id: data.id,
      callback: () => onRemoveStatus(data, null),
    };

    dispatch(AppDetailsActions.appListRemoveStatus.action(requestData));
  };

  return (
    <AppCardContentStyled className={clsx('app-card-content')}>
      <AppCard
        data={data}
        className={className}
        onUpdateStatus={onUpdateStatus}
        onRemoveStatus={onRemoveStatus}
        location={location}
        LinkProps={LinkProps}
        hideControls={hideControls}
      />
      <div className="app-card-content__section">
        {hasGenres && (
          <div>
            <Typography className="app-card-content__title p4" component="h6" color="textSecondary">
              {t('common.genresLabel')}
            </Typography>
            <div className="app-card-content__list app-card-content__list--genres">
              <TruncatedList
                ListComponent={Grid}
                ListComponentProps={{
                  container: true,
                  spacing: 1,
                }}
                ListItemComponent={Grid}
                ListItemComponentProps={{
                  item: true,
                }}
                renderTruncator={({ hiddenItemsCount }) => <Chip label={`+${hiddenItemsCount}`} />}
              >
                {genresData.map(({ name, rest_count } = {}, idx) => {
                  return <Chip key={idx} label={rest_count || name} />;
                })}
              </TruncatedList>
            </div>
          </div>
        )}
        {hasDevices && (
          <div className="m-t-15">
            <Typography className="app-card-content__title p4" component="h6" color="textSecondary">
              {t('common.availableOn')}
            </Typography>
            {sizedDevices}
          </div>
        )}
        {useAuthorizing && !isSubscribed && !isSimpleView && !hideControls && (
          <Guard iframe={false}>
            <AddRemoveButton
              className="m-t-15 app-card-content__button purple"
              onClick={handleUpdateAppListStatus}
              handleDelete={handleRemoveAppListStatus}
              added={added}
              minWidth={145}
              size="medium"
              color="primary"
              variant="contained"
              fullWidth
              disabled={statusesLoadings[data.id]}
              loading={statusesLoadings[data.id]}
              checkAuth
              checkAuthProps={{
                authComponentProps: {
                  hint: t('common.trackServices'),
                  analyticsData: {
                    triggered: triggered.app,
                    action: actions.addToApps,
                    app_id: data.id,
                    partner_id: partner.id || 0,
                    location,
                  },
                },
              }}
            >
              {t(`buttons.${added ? 'inMyApps' : 'addToMyApps'}`)}
            </AddRemoveButton>
          </Guard>
        )}
        {useSubscription && isSubscribed && !hideControls && (
          <Guard iframe={false}>
            <Button
              className="m-t-15 app-card-content__button purple"
              size="medium"
              color="primary"
              variant="contained"
              fullWidth
              component={Link}
              to={getLink(ROUTE_PROFILE_SUBSCRIPTION_DETAIL, { slug: data.unique_slug })}
              location={location}
            >
              {t('buttons.manage')}
            </Button>
          </Guard>
        )}
      </div>
    </AppCardContentStyled>
  );
}

AppCardContent.defaultProps = {
  className: '',
  location: '',
  onUpdateStatus: () => {},
  onRemoveStatus: () => {},
  LinkProps: {},
  hideControls: false,
};

AppCardContent.propTypes = {
  data: PropTypes.object.isRequired,
  className: PropTypes.string,
  location: PropTypes.string,
  onUpdateStatus: PropTypes.func,
  onRemoveStatus: PropTypes.func,
  LinkProps: PropTypes.object,
  hideControls: PropTypes.bool,
};

export default React.memo(AppCardContent);
